import React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

interface TooltipProps {
  /** Content shown in the tooltip. */
  content: React.ReactNode;
  /** Child element(s) that trigger the tooltip on hover/focus. */
  children: React.ReactNode;
  /** Optional side: top | right | bottom | left. Default: top */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Delay before showing (ms). Default from provider. */
  delayDuration?: number;
}

/**
 * Accessible tooltip using Radix UI. Wrap any trigger element; tooltip shows on hover/focus.
 * Use TooltipProvider at layout level (e.g. AdminLayout) for delay and accessibility.
 */
export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  side = 'top',
  delayDuration,
}) => (
  <TooltipPrimitive.Root delayDuration={delayDuration}>
    <TooltipPrimitive.Trigger asChild>
      {children}
    </TooltipPrimitive.Trigger>
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        side={side}
        sideOffset={6}
        className="z-[120] max-w-xs rounded-md bg-gray-900 px-3 py-2 text-sm text-white shadow-md"
      >
        {content}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  </TooltipPrimitive.Root>
);

/** Wrap the admin app (or section) so Tooltip delay and behavior work. */
export const TooltipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <TooltipPrimitive.Provider delayDuration={300} skipDelayDuration={0}>
    {children}
  </TooltipPrimitive.Provider>
);
